/**
 * TREATX-DOCTOR-MODAL-01 — Patient-facing Doctor Detail Modal.
 *
 * Composes ALL fields from the canonical PCS doctor projection
 * (`clinic.doctors.public_verified_doctors[]`). NO new fetch, NO new
 * SSOT, NO hardcoded data, NO mock fallback, NO parallel projection.
 *
 *   Database  →  ComplianceSummaryService.build_summary  →
 *   provider_compliance_summary  →  GET /api/search/providers  →
 *   clinic.doctors.public_verified_doctors[]  →  this modal.
 *
 * The "Verified Documents Gallery" sources file_url from the canonical
 * `clinic_verification_documents` collection (linked_entity_type=person),
 * forwarded by the additive PCS projection. Image enlargement uses the
 * same shadcn Dialog primitive used by AccreditationEvidenceModal.
 */
import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import {
  User, Stethoscope, Clock, Globe, Award, GraduationCap, ShieldCheck,
  FileText, Building2, ExternalLink, ImageIcon, X,
} from 'lucide-react';

const fmt = (s) => (s || '').toString().replace(/[-_]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

/**
 * SEARCH-V2-DOCTOR-MODAL-POLISH-01 — Detect non-meaningful text so the
 * patient never sees raw placeholders, template tokens, "undefined" /
 * "null" / "N/A" literals, or internal sprint fingerprints that leaked
 * into seeded copy.
 *
 * Returns true ONLY for text we are confident is presentable to a patient.
 */
const TEMPLATE_TOKEN_RE = /\{\{[^}]*\}\}|\$\{[^}]*\}|<%[\s\S]*?%>/;
const INTERNAL_FINGERPRINT_RE = /\b(DOCTOR-MODAL-0\d|Proof-[A-Z]\b|canonical biography token|sprint-fingerprint)\b/i;
const isMeaningfulText = (value) => {
  if (value === null || value === undefined) return false;
  const s = String(value).trim();
  if (!s) return false;
  const low = s.toLowerCase();
  if (low === 'null' || low === 'undefined' || low === 'n/a' || low === 'na' || low === 'tbd' || low === 'tba') return false;
  if (TEMPLATE_TOKEN_RE.test(s)) return false;
  if (INTERNAL_FINGERPRINT_RE.test(s)) return false;
  return true;
};

/**
 * Image wrapper that swaps to the provided fallback element on load
 * failure (404, CORS, non-image MIME, decode error). Keeps frame size
 * stable so layout never jumps.
 */
function SafeImage({ src, alt, className, testId, fallback }) {
  const [failed, setFailed] = useState(false);
  if (!src || failed) return fallback;
  return (
    <img
      src={src}
      alt={alt}
      className={className}
      data-testid={testId}
      onError={() => setFailed(true)}
      loading="lazy"
    />
  );
}

const _DOCUMENT_TYPE_LABEL = {
  medical_diploma: 'Medical Diploma',
  board_certificate: 'Board / Society Certificate',
  membership_evidence: 'Membership Evidence',
  license: 'Medical License',
  fellowship_certificate: 'Fellowship Certificate',
};

function ImageLightbox({ open, onClose, src, title }) {
  return (
    <Dialog open={!!open} onOpenChange={(v) => { if (!v) onClose && onClose(); }}>
      <DialogContent
        className="max-w-3xl p-2 bg-black/95 border-0"
        data-testid="doctor-document-lightbox"
      >
        <DialogHeader className="px-2">
          <DialogTitle className="text-white text-sm" data-testid="doctor-document-lightbox-title">
            {title || 'Verified document'}
          </DialogTitle>
          <DialogDescription className="sr-only">{title}</DialogDescription>
        </DialogHeader>
        <div className="flex items-center justify-center bg-black/40 rounded-md overflow-hidden">
          {src ? (
            <img
              src={src}
              alt={title || 'verified document'}
              className="max-h-[75vh] max-w-full object-contain"
              data-testid="doctor-document-lightbox-image"
            />
          ) : (
            <div className="h-72 flex items-center justify-center text-white/60">
              <ImageIcon className="h-10 w-10" />
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function DoctorDetailModal({ open, doctor, clinic, treatment, onClose, onRequest }) {
  const [lightbox, setLightbox] = useState(null);
  const d = doctor || {};
  const c = clinic || {};

  if (!doctor) return null;

  const primarySpec = (d.specialties || [])[0];
  const langs = (Array.isArray(d.languages) ? d.languages : []).filter(isMeaningfulText);
  const certs = (Array.isArray(d.certifications) ? d.certifications : []).filter(
    (c) => c && isMeaningfulText(c.name),
  );
  const edu = (Array.isArray(d.education) ? d.education : []).filter(
    (e) => e && (isMeaningfulText(e.degree) || isMeaningfulText(e.title) || isMeaningfulText(e.institution)),
  );
  const docs = Array.isArray(d.verified_documents) ? d.verified_documents : [];
  const hasBio = isMeaningfulText(d.bio);
  const safeName = isMeaningfulText(d.full_name) ? d.full_name : 'Doctor';

  const verifiedBoardOrMembership = certs.filter(
    (x) => ['board', 'membership', 'professional_society'].includes((x?.type || '').toLowerCase()),
  );

  const treatmentEligible = (() => {
    if (!treatment) return true;
    const tx = String(treatment).toLowerCase();
    return (d.specialties || []).some((s) => {
      const ss = String(s).toLowerCase();
      return ss === tx || tx.includes(ss) || ss.includes(tx.split('-')[0]);
    });
  })();

  const providerId = c.provider_id || c.clinic_id || c.id;
  const handleViewProfile = () => {
    if (providerId) window.location.hash = `#/clinics/${providerId}`;
    onClose && onClose();
  };

  return (
    <>
      <Dialog open={!!open} onOpenChange={(v) => { if (!v) onClose && onClose(); }}>
        <DialogContent
          className="max-w-3xl max-h-[92vh] overflow-y-auto p-0 gap-0 [&>button]:text-white [&>button]:opacity-90 [&>button]:hover:opacity-100 [&>button]:focus:ring-white"
          data-testid={`doctor-detail-modal-${d.doctor_id || ''}`}
          data-render-authority="DoctorDetailModal-v1"
          data-render-generation="DOCTOR-MODAL-01"
          data-sprint-fingerprint="DOCTOR-MODAL-01"
        >
          {/* Hero header */}
          <div className="bg-gradient-to-br from-[#0E2A47] via-[#13355A] to-[#27557A] text-white p-6 rounded-t-lg">
            <div className="flex items-start gap-4">
              <div className="flex-none w-20 h-20 rounded-full overflow-hidden border-2 border-white/80 bg-white/10 shadow-lg">
                <SafeImage
                  src={d.profile_photo_url}
                  alt={safeName}
                  className="w-full h-full object-cover"
                  testId="doctor-modal-photo"
                  fallback={(
                    <div className="w-full h-full flex items-center justify-center text-white/70" data-testid="doctor-modal-photo-fallback">
                      <User className="h-9 w-9" />
                    </div>
                  )}
                />
              </div>
              <div className="flex-1 min-w-0">
                <DialogHeader className="text-left space-y-1">
                  <DialogTitle className="text-xl font-bold leading-tight" data-testid="doctor-modal-name">
                    {d.full_name || 'Doctor'}
                  </DialogTitle>
                  <DialogDescription className="sr-only">
                    Detailed profile for {d.full_name}
                  </DialogDescription>
                </DialogHeader>
                <div className="flex items-center flex-wrap gap-2 mt-1 text-sm">
                  {primarySpec && (
                    <span className="inline-flex items-center gap-1 text-white/90" data-testid="doctor-modal-specialty">
                      <Stethoscope className="h-3.5 w-3.5" />
                      {fmt(primarySpec)}
                    </span>
                  )}
                  {(d.years_of_experience || 0) > 0 && (
                    <span className="inline-flex items-center gap-1 text-white/85" data-testid="doctor-modal-experience">
                      <Clock className="h-3.5 w-3.5" />
                      {d.years_of_experience}+ years experience
                    </span>
                  )}
                </div>
                {verifiedBoardOrMembership.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-2" data-testid="doctor-modal-board-badges">
                    {verifiedBoardOrMembership.slice(0, 4).map((b, i) => (
                      <Badge
                        key={`mb-${i}`}
                        className="bg-white/15 text-white border border-white/30 text-[10px]"
                        title={b.issuing_body || b.name}
                      >
                        <ShieldCheck className="h-2.5 w-2.5 mr-1" />
                        {b.slug ? b.slug.toUpperCase() : (b.name || '').slice(0, 22)}
                      </Badge>
                    ))}
                  </div>
                )}
                {treatment && !treatmentEligible && (
                  <p className="text-[11px] text-amber-200 mt-2" data-testid="doctor-modal-not-eligible">
                    Not affiliated with {fmt(treatment)} on this clinic's roster.
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Sections */}
          <div className="p-6 space-y-5 bg-white">
            {/* Biography */}
            {hasBio && (
              <section data-testid="doctor-modal-bio">
                <h3 className="font-semibold text-slate-800 text-sm mb-1.5 flex items-center gap-2">
                  <User className="h-4 w-4 text-[#3BA4C5]" />
                  Biography
                </h3>
                <p className="text-sm text-slate-700 leading-relaxed">{d.bio}</p>
              </section>
            )}

            {/* Education */}
            {edu.length > 0 && (
              <section data-testid="doctor-modal-education">
                <h3 className="font-semibold text-slate-800 text-sm mb-2 flex items-center gap-2">
                  <GraduationCap className="h-4 w-4 text-[#3BA4C5]" />
                  Education
                </h3>
                <ul className="space-y-2">
                  {edu.map((e, i) => (
                    <li
                      key={`edu-${i}`}
                      className="text-sm flex items-start gap-2 text-slate-700"
                      data-testid={`doctor-modal-education-row-${i}`}
                    >
                      <span className="inline-flex w-1.5 h-1.5 rounded-full bg-[#3BA4C5] mt-2 flex-none" />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-slate-900">
                          {isMeaningfulText(e.degree) ? e.degree
                            : isMeaningfulText(e.title) ? e.title
                            : isMeaningfulText(e.type) ? fmt(e.type)
                            : 'Qualification'}
                        </p>
                        {(isMeaningfulText(e.institution) || isMeaningfulText(e.year) || isMeaningfulText(e.field_of_study)) && (
                          <p className="text-xs text-slate-600">
                            {[
                              isMeaningfulText(e.institution) ? e.institution : null,
                              isMeaningfulText(e.year) ? String(e.year) : null,
                              isMeaningfulText(e.field_of_study) ? e.field_of_study : null,
                            ].filter(Boolean).join(' · ')}
                          </p>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {/* Board Certifications & Memberships */}
            {certs.length > 0 && (
              <section data-testid="doctor-modal-certifications">
                <h3 className="font-semibold text-slate-800 text-sm mb-2 flex items-center gap-2">
                  <Award className="h-4 w-4 text-emerald-600" />
                  Board Certifications & Memberships
                </h3>
                <div className="grid sm:grid-cols-2 gap-2">
                  {certs.map((c, i) => (
                    <div
                      key={`cert-${i}`}
                      className="flex items-start gap-2 p-2.5 bg-emerald-50/60 border border-emerald-200 rounded-md"
                      data-testid={`doctor-modal-cert-row-${i}`}
                    >
                      <ShieldCheck className="h-3.5 w-3.5 text-emerald-600 mt-0.5 flex-none" />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-slate-900 text-xs">{c.name}</p>
                        <p className="text-[10px] text-emerald-700 truncate">
                          {c.issuing_body || ''}
                          {c.issued_year ? ` · ${c.issued_year}` : ''}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Languages */}
            {langs.length > 0 && (
              <section data-testid="doctor-modal-languages">
                <h3 className="font-semibold text-slate-800 text-sm mb-2 flex items-center gap-2">
                  <Globe className="h-4 w-4 text-[#3BA4C5]" />
                  Languages
                </h3>
                <div className="flex flex-wrap gap-1.5">
                  {langs.map((l) => (
                    <Badge
                      key={`lang-${l}`}
                      className="bg-[#3BA4C5]/10 text-[#0E2A47] border border-[#3BA4C5]/30 text-[10px]"
                      data-testid={`doctor-modal-language-${l}`}
                    >
                      {l}
                    </Badge>
                  ))}
                </div>
              </section>
            )}

            {/* Verified Documents Gallery */}
            <section data-testid="doctor-modal-documents">
              <h3 className="font-semibold text-slate-800 text-sm mb-2 flex items-center gap-2">
                <FileText className="h-4 w-4 text-emerald-600" />
                Verified Documents
                <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200 text-[10px]" data-testid="doctor-modal-documents-count">
                  {docs.length}
                </Badge>
              </h3>
              {docs.length === 0 ? (
                <p className="text-xs text-slate-500 italic" data-testid="doctor-modal-documents-empty">
                  No verified documents uploaded yet.
                </p>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {docs.map((doc, i) => (
                    <button
                      type="button"
                      key={`doc-${i}`}
                      onClick={() => setLightbox(doc)}
                      className="group text-left flex flex-col rounded-lg border border-slate-200 overflow-hidden bg-white hover:border-emerald-400 hover:shadow-sm transition-all"
                      data-testid={`doctor-modal-document-${i}`}
                      title={`Click to enlarge: ${doc.document_name}`}
                    >
                      <div className="aspect-[4/3] bg-slate-100 overflow-hidden">
                        <SafeImage
                          src={doc.file_url}
                          alt={doc.document_name}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                          testId={`doctor-modal-document-thumb-${i}`}
                          fallback={(
                            <div className="w-full h-full flex items-center justify-center text-slate-300" data-testid={`doctor-modal-document-thumb-fallback-${i}`}>
                              <ImageIcon className="h-8 w-8" />
                            </div>
                          )}
                        />
                      </div>
                      <div className="p-2">
                        <p className="text-[11px] font-semibold text-slate-800 truncate">
                          {_DOCUMENT_TYPE_LABEL[doc.document_type] || fmt(doc.document_type) || 'Document'}
                        </p>
                        <p className="text-[10px] text-slate-500 truncate">
                          {doc.document_name}
                        </p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </section>

            {/* Affiliated Clinic */}
            {c?.name && (
              <section data-testid="doctor-modal-affiliation">
                <h3 className="font-semibold text-slate-800 text-sm mb-2 flex items-center gap-2">
                  <Building2 className="h-4 w-4 text-slate-500" />
                  Affiliated Clinic
                </h3>
                <div className="flex items-center gap-3 p-2.5 bg-slate-50 border border-slate-200 rounded-md">
                  <div className="flex-none w-10 h-10 rounded-md overflow-hidden bg-white border border-slate-200">
                    <SafeImage
                      src={c.logo}
                      alt={c.name}
                      className="w-full h-full object-cover"
                      testId="doctor-modal-clinic-logo"
                      fallback={(
                        <div className="w-full h-full flex items-center justify-center text-slate-300" data-testid="doctor-modal-clinic-logo-fallback">
                          <Building2 className="h-5 w-5" />
                        </div>
                      )}
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-slate-900 text-sm truncate">{c.name}</p>
                    <p className="text-[10px] text-slate-500 truncate">
                      {[c.city, c.country].filter(Boolean).join(', ')}
                    </p>
                  </div>
                </div>
              </section>
            )}

            {/* CTA band */}
            <div className="flex flex-col sm:flex-row gap-2 pt-2 border-t border-slate-100">
              <Button
                onClick={() => { onRequest && onRequest(d, c); }}
                className="flex-1 bg-[#3BA4C5] hover:bg-[#2E8AA5] text-white"
                data-testid="doctor-modal-request-offer-btn"
              >
                Request offer from this clinic
              </Button>
              <Button
                variant="outline"
                onClick={handleViewProfile}
                className="flex-1"
                data-testid="doctor-modal-view-profile-btn"
              >
                <ExternalLink className="h-4 w-4 mr-2" />
                View clinic profile
              </Button>
              <Button
                variant="ghost"
                onClick={onClose}
                className="sm:w-auto"
                data-testid="doctor-modal-close-btn"
              >
                <X className="h-4 w-4 mr-1" />
                Close
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <ImageLightbox
        open={!!lightbox}
        src={lightbox?.file_url}
        title={lightbox?.document_name}
        onClose={() => setLightbox(null)}
      />
    </>
  );
}
